/* eslint-disable max-len */
import React, {useState, useEffect} from 'react';
import {injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';
import {Form, Formik} from 'formik';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Button,
  TextField,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Grid,
} from '@mui/material';
import {Breakpoints as breakpoints, EzFormControl, YesNoRadioGroup} from 'wobi-web-common';
import {validIsraeliId} from 'wobi-web-common/dist/components/utils/validation';
import {utils} from '../../utils/offerFieldsActions';
import {CREDITCARD_OWNER_RELATION_SELECT_OPTIONS} from '../../constants';
import useStyles from './useStyles';

// Constants
const INITIAL_PAYMENT_VALUES = {
  cardHolder: '',
  creditCardNumber: '',
  creditcardOwnerRelationToPolicyOwner: '',
  creditCardType: '',
  cvv: '',
  identification: '',
  isCCOwnerIsThePolicyOwner: 'yes',
  month: '',
  paymentCasco: '1',
  paymentMandatory: '1',
  year: '',
};

// Utility functions
const generatePaymentOptions = () => {
  const items = [];
  for (let i = 0; i < 12; i++) {
    items.push(i + 1);
  }
  return items;
};

const getCreditCardNumberClass = (error, touched) => {
  if (!error) {
    return 'helperText';
  }
  if (error && touched) {
    return 'helperText err';
  }
  return '';
};

// Validation schema creator
const createPaymentValidationSchema = (messages) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  return yup.object().shape({
    cardHolder: yup.string()
      .test('len', messages('validation.required'), (val) => {
        if (val) {
          return val.split(' ').filter(item => item !== '').length === 2;
        }
        return true;
      })
      .required(messages('validation.required')),
    creditCardNumber: yup.string()
      .matches(/^\d{14,16}$/, messages('validation.invalid_length'))
      .required(messages('validation.required')),
    creditcardOwnerRelationToPolicyOwner: yup.string().when('isCCOwnerIsThePolicyOwner', {
      is: (value) => value === 'no',
      then: yup.string().required(messages('validation.required')),
    }),
    cvv: yup.string()
      .matches(/^\d{3,4}$/, messages('validation.invalid_length'))
      .required(messages('validation.required')),
    identification: validIsraeliId,
    month: yup.string()
      .required(messages('validation.required'))
      .test('valid-month', messages('validation.date'), function (value) {
        const {year} = this.parent;
        if (year === currentYear && Number.parseInt(value, 10) < currentMonth) {
          return false;
        }
        return true;
      }),

    paymentCasco: yup.string().required(messages('validation.required')),
    paymentMandatory: yup.string().required(messages('validation.required')),
    year: yup.string()
      .required(messages('validation.required')),
  });
};

// Sub-components for cleaner structure
const CreditCardOwnerRelation = ({values, messages, handleChange, classes}) => {
  if (values.isCCOwnerIsThePolicyOwner !== 'no') {
    return null;
  }

  return (
    <Grid
      size={{
        md: 6
      }}>
      <EzFormControl
        name='creditcardOwnerRelationToPolicyOwner'
        label={messages('fields.creditcardOwnerRelationToPolicyOwner')}
        labelFor='creditcard-creditcardOwnerRelationToPolicyOwner'
        isLabelInChild
      >
        <TextField
          select
          id='creditcard-creditcardOwnerRelationToPolicyOwner'
          labelId='creditcardOwnerRelationToPolicyOwner-label'
          value={values.creditcardOwnerRelationToPolicyOwner}
          onChange={handleChange}
          size='small'
          fullWidth
          InputLabelProps={{className: classes.inputLabel}}
        >
          {CREDITCARD_OWNER_RELATION_SELECT_OPTIONS.map(relation => (
            <MenuItem key={`relation-${relation.value}`} value={`${relation.value}`}>
              {relation.name}
            </MenuItem>
          ))}
        </TextField>
      </EzFormControl>
    </Grid>
  );
};

// Main component
const PaymentContainer = injectIntl(withRouter(({
  intl,
  isVerifyingPayment,
  onPaymentSubmit,
  children,
  displayUserOwnCardFields,
  formKey, // Add a key prop to force re-render
  isUseCardForBoth,
  isFirstCard,
  policyType,
  isAsset,
}) => {
  // Hooks and utilities
  const messages = (id) => intl.formatMessage({id});
  const isMobile = useMediaQuery(`(max-width:${breakpoints.mobile}px)`);
  const { classes } = useStyles({isMobile});
  const [numberOfPayments, setNumberOfPayments] = useState([]);
  const showMandatoryPaymentsDropdown = isFirstCard || isUseCardForBoth;
  const showCascoPaymentsDropdown = !isFirstCard || isUseCardForBoth;

  // Constants
  const yearsOptions = utils.getYearsOptions();
  const monthsOptions = utils.getMonthsOptions();
  const yesNoOverriddenStyles = {
    inlineLabel: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
  };

  // Event handlers
  const handleIsCCOwnerIsThePolicyOwner = (event, setFieldValue, handleChange) => {
    const value = event.target.value;
    if (value === 'yes') {
      setFieldValue('creditcardOwnerRelationToPolicyOwner', '');
    }
    handleChange(event);
  };

  const handlePaymentChange = (value, setFieldValue, paymentType) => {
    setFieldValue('payment' + paymentType, value);
  };

  // Effects
  useEffect(() => {
    setNumberOfPayments(generatePaymentOptions());
  }, []);

  // Handle form submission with wrapping
  // const handleSubmitWithReset = async (values, formikHelpers) => {
  //   await onPaymentSubmit(values, formikHelpers);
  // };

  return (
    <div className={classes.purchaseContainer}>
      <Formik

        onSubmit={onPaymentSubmit}
        validationSchema={createPaymentValidationSchema(messages)}
        initialValues={INITIAL_PAYMENT_VALUES}
        key={formKey} // Add key to Formik to force re-render
        enableReinitialize // Enable reinitialization
      >
        {({values, errors, touched, handleChange, handleSubmit, setFieldValue}) => (
          <Form onSubmit={handleSubmit} autoComplete='on' >
            <Grid
              container
              spacing={2}
              className={classes.form}
            >

              {/* Additional Owner Fields */}
              {displayUserOwnCardFields && (
                <Grid
                  size={{
                    md: 6
                  }}>
                  <EzFormControl
                    name='isCCOwnerIsThePolicyOwner'
                    label={messages('fields.isCreditcardOwnerIsThePolicyOwner')}
                    isInlineLabel
                    overriddenStyles={yesNoOverriddenStyles}
                  >
                    <YesNoRadioGroup
                      value={values.isCCOwnerIsThePolicyOwner}
                      onChange={(event) => handleIsCCOwnerIsThePolicyOwner(event, setFieldValue, handleChange)}
                    />
                  </EzFormControl>

                  <CreditCardOwnerRelation
                    values={values}
                    messages={messages}
                    handleChange={handleChange}
                    classes={classes}
                  />
                </Grid>
              )}
              {/* Card Holder Section */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 5
                }}>
                <EzFormControl
                  name='cardHolder'
                  label={messages('fields.cardOwner')}
                  labelFor='cardHolder'
                  isLabelInChild
                >
                  <TextField
                    id='cardHolder'
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/[^ A-Za-zא-ת]/g, '');
                      handleChange(e);
                    }}
                    value={values.cardHolder}
                    size='small'
                    fullWidth
                    InputLabelProps={{className: classes.inputLabel}}
                    autoComplete='cc-name'
                  />
                </EzFormControl>
              </Grid>
              {/* Credit Card Number Section */}
              <Grid
                className={getCreditCardNumberClass(errors.creditCardNumber, touched.creditCardNumber)}
                size={{
                  xs: 12,
                  md: 12,
                  lg: 6
                }}>
                {/* Credit Card Number Input */}
                <EzFormControl
                  name='creditCardNumber'
                  label={messages('fields.credit_card_number')}
                  labelFor='creditCardNumber'
                  isLabelInChild
                >
                  <TextField
                    id='creditCardNumber'
                    className={classes.creditCardInput}
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, '');
                      handleChange(e);
                    }}
                    value={values.creditCardNumber}
                    size='small'
                    fullWidth
                    InputLabelProps={{className: classes.inputLabel}}
                    autoComplete='cc-number'
                  />
                </EzFormControl>
                <FormHelperText className={classes.creditCardHelp}>
                  {messages('fields.credit_card_number.helpText')}
                </FormHelperText>
              </Grid>
              {/* Card Details Section */}
              <Grid
                size={{
                  xs: 4,
                  sm: 6,
                  lg: 3
                }}>
                <input
                  type='text'
                  name='cc-exp'
                  id='cc-exp'
                  autoComplete='cc-exp'
                  inputMode='numeric'
                  pattern='[0-9]{2}/[0-9]{2}'
                  placeholder='MM/YY'
                  style={{
                    ariaHidden: 'true',
                    height: '0px',
                    opacity: 1,
                    position: 'fixed',
                    width: '100%',
                    zIndex: -10,
                  }}
                  onChange={(e) => {
                    const [mm, yy] = e.target.value.split('/');
                    const paddedMonth = mm?.padStart(2, '0');
                    const fullYear = yy?.length === 2 ? `20${yy}` : yy;

                    if (paddedMonth && fullYear) {
                      setFieldValue('month', Number.parseInt(paddedMonth, 10));
                      setFieldValue('year', Number.parseInt(fullYear, 10));
                    }
                  }}
                />
                <EzFormControl
                  name='year'
                  label={messages('fields.card_year')}
                  labelFor='creditcard-year'
                  isLabelInChild
                >
                  <TextField
                    select
                    id='creditcard-year'
                    labelId='year-label'
                    value={values.year}
                    onChange={(e) => setFieldValue('year', e.target.value)}
                    size='small'
                    fullWidth
                    InputLabelProps={{className: classes.inputLabel}}
                    name='cc-exp-year'
                    autoComplete='cc-exp-year'
                  >
                    {yearsOptions.map(year => (
                      <MenuItem key={`year-${year}`} value={year}>{year}</MenuItem>
                    ))}
                  </TextField>
                </EzFormControl>
              </Grid>

              <Grid
                size={{
                  xs: 4,
                  sm: 6,
                  lg: 3
                }}>
                <EzFormControl
                  name='month'
                  label={messages('fields.card_month')}
                  labelFor='creditcard-month'
                  isLabelInChild
                >
                  <TextField
                    select
                    id='creditcard-month'
                    labelId='month-label'
                    value={values.month}
                    onChange={(e) => setFieldValue('month', e.target.value)}
                    fullWidth
                    size='small'
                    InputLabelProps={{className: classes.inputLabel}}
                    name='cc-exp-month'
                    autoComplete='cc-exp-month'
                  >
                    {monthsOptions.map(month => (
                      <MenuItem key={`month-${month}`} value={month}>{month}</MenuItem>
                    ))}
                  </TextField>
                </EzFormControl>
              </Grid>

              <Grid
                size={{
                  xs: 4,
                  sm: 6,
                  lg: 3
                }}>
                <EzFormControl
                  name='cvv'
                  label={messages('fields.cvv')}
                  labelFor='cvv'
                  isLabelInChild
                >
                  <TextField
                    id='cvv'
                    onChange={e => {
                      e.target.value = e.target.value.replace(/\D/g, '');
                      handleChange(e);
                    }}
                    value={values.cvv}
                    type='cvv'
                    inputProps={{maxLength: '4'}}
                    size='small'
                    fullWidth
                    InputLabelProps={{className: classes.inputLabel}}
                    autoComplete='cc-csc'
                  />
                </EzFormControl>
              </Grid>

              {/* Identification Section */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <EzFormControl
                  name='identification'
                  label={messages('fields.identification')}
                  labelFor='identification'
                  isLabelInChild
                >
                  <TextField
                    id='identification'
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, '');
                      handleChange(e);
                    }}
                    value={values.identification}
                    size='small'
                    fullWidth
                    InputLabelProps={{className: classes.inputLabel}}
                  />
                </EzFormControl>
              </Grid>

              {/* Payment Section */}
              <Grid
                size={{
                  xs: 12,
                  lg: 6
                }}>
                {showMandatoryPaymentsDropdown && !isAsset && <EzFormControl
                  name='payment'
                  isLabelInChild
                  label={messages('fields.payments.mandatory')}
                  labelFor='creditcard-payment'
                >
                  <TextField
                    select
                    id='creditcard-paymentMandatory'
                    value={values.paymentMandatory}
                    onChange={(e) => handlePaymentChange(e.target.value, setFieldValue, 'Mandatory')}
                    style={{
                      flex: '1 0 auto',
                      minWidth: 135,
                    }}
                    size='small'
                    InputLabelProps={{className: classes.inputLabel}}
                  >
                    {numberOfPayments.map(item => (
                      <MenuItem key={`paymentMandatory-${item}`} value={item}>{item}</MenuItem>
                    ))}
                  </TextField>
                </EzFormControl>}
                {showCascoPaymentsDropdown && <EzFormControl
                  name='payment'
                  isLabelInChild
                  label={messages('fields.payments.casco.prefix') + policyType}
                  labelFor='creditcard-payment'
                >
                  <TextField
                    select
                    id='creditcard-paymentCasco'
                    value={values.paymentCasco}
                    onChange={(e) => handlePaymentChange(e.target.value, setFieldValue, 'Casco')}
                    style={{
                      flex: '1 0 auto',
                      minWidth: 135,
                    }}
                    size='small'
                    InputLabelProps={{className: classes.inputLabel}}
                  >
                    {numberOfPayments.map(item => (
                      <MenuItem key={`paymentCasco-${item}`} value={item}>{item}</MenuItem>
                    ))}
                  </TextField>
                </EzFormControl>}
              </Grid>

              {/* Children Elements here you can add fields thats are not spoused to be the part of every ui and depend on outer state    */}
              {children}

              {/* Submit Button Section */}
              <Grid
                className={classes.purchaseBtnWrapper}
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Button
                  className={classes.paymentBtn}
                  type='submit'
                  variant='contained'
                  disabled={isVerifyingPayment}
                  data-testid='purchase-btn'
                >
                  {isFirstCard && !isUseCardForBoth ? messages('secure.payment.payment_continue_btn') : messages('secure.payment.payment_end_btn')}
                </Button>
                {isVerifyingPayment && (
                  <CircularProgress className={classes.verifyLoader} size={30} />
                )}
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
}));

export default PaymentContainer;
